/**
 * Hold-to-read: the logic behind reaching, on a touch screen, what a mouse gets by hovering.
 *
 * The app leans on `title` in about ninety places, and every one of them is invisible on a phone.
 * Some are decoration; several are not. The board's greyed `+` buttons carry the NAME OF THE
 * PERSON allowed to use them (11a); a board cell carries whose pick it was before it was traded;
 * a farm cell carries who won the player and for how much. On draft night those are answers to
 * questions people ask out loud, and half the room is on a phone.
 *
 * Rather than convert ninety call sites into a bespoke component, the mechanism is one document
 * listener that reads whatever the pressed element already declares. So every `title` in the app
 * became reachable at once, and a new one is reachable the moment it is written.
 *
 * This module is the part with no DOM in it, so it can be tested without adding jsdom to a suite
 * that has never needed it. `HintNode` is the shape both a real `Element` and a fake one satisfy.
 */

/** How long a finger has to stay down. Long enough not to fire on a tap, short enough to feel
 *  deliberate rather than broken. Android's own long-press is ~500ms; a hair under it means the
 *  bubble is up before the browser starts thinking about its own callout. */
export const HOLD_MS = 420;

/** A press that travels this far was a scroll, not a hold. */
export const MOVE_CANCEL_PX = 10;

/** The bubble's distance from the element it describes. */
export const HINT_GAP = 8;

export interface HintNode {
  tagName: string;
  getAttribute(name: string): string | null;
  parentElement: HintNode | null;
}

/**
 * Elements a hold belongs to already. Holding on a text field is how you reach select-all and the
 * paste menu, and taking that over to show a tooltip would be a straight loss.
 */
const OWN_THE_HOLD = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION']);

/**
 * The nearest ancestor that has something to say, and what it says.
 *
 * `data-hint` wins over `title` so a screen can give the phone a longer sentence than the one it
 * gives the mouse — and, where a `title` is only there to repeat the visible label, can say
 * `data-hint=""` to opt out rather than have the bubble state the obvious.
 */
export function hintFor(start: HintNode | null): { text: string; node: HintNode } | null {
  for (let n = start; n; n = n.parentElement) {
    if (OWN_THE_HOLD.has(n.tagName.toUpperCase())) return null;
    if (n.getAttribute('contenteditable') != null) return null;

    const hint = n.getAttribute('data-hint');
    if (hint != null) {
      const t = hint.trim();
      // An explicit empty `data-hint` is a decision: this subtree has nothing to add.
      return t ? { text: t, node: n } : null;
    }

    const title = n.getAttribute('title');
    if (title != null && title.trim()) return { text: title.trim(), node: n };
  }
  return null;
}

export interface Rect { left: number; top: number; width: number; height: number }
export interface Placed { left: number; top: number; above: boolean }

/**
 * Where to put the bubble.
 *
 * Below the element by default, because a finger is on top of it. Above when there is no room
 * below — which on a phone, with a fixed tab bar eating the last 60px, is most of the screen.
 * Clamped to the viewport at both edges: a hint that runs off the side of a 390px screen is the
 * same as no hint, and the elements that most need one are the outermost columns of a grid.
 */
export function placeHint(
  el: Rect,
  bubble: { width: number; height: number },
  view: { width: number; height: number },
  margin = 8,
): Placed {
  const below = el.top + el.height + HINT_GAP;
  const above = below + bubble.height + margin > view.height;
  const top = above
    ? Math.max(margin, el.top - HINT_GAP - bubble.height)
    : below;

  const wanted = el.left + el.width / 2 - bubble.width / 2;
  const left = Math.min(
    Math.max(margin, wanted),
    Math.max(margin, view.width - bubble.width - margin),
  );

  return { left, top, above };
}

/** Whether a press has moved far enough to be a scroll rather than a hold. */
export function movedTooFar(
  from: { x: number; y: number },
  to: { x: number; y: number },
  limit = MOVE_CANCEL_PX,
): boolean {
  return Math.abs(to.x - from.x) > limit || Math.abs(to.y - from.y) > limit;
}
